import React, { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useLocale, SupportedLocale } from '@platform/core';
import { useSimpleAuth } from '../contexts/SimpleAuthContext';
import { usePlanning } from '../contexts/PlanningContext';

const LANGUAGES: { code: SupportedLocale; label: string }[] = [
  { code: 'he', label: 'עברית' },
  { code: 'en', label: 'English' },
];

export default function Layout() {
  const { simpleUser, signOut } = useSimpleAuth();
  const { stats, companies, currentCompany, selectCompany } = usePlanning();
  const { t, locale, setLocale, direction } = useLocale();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Close mobile menu on navigation
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  const navItems = [
    { path: '/companies', label: t('planning.companies') || 'Companies', icon: '🏢' },
    { path: '/plans', label: t('planning.title'), icon: '📋' },
    { path: '/calendar', label: t('planning.calendar') || 'Calendar', icon: '📅' },
    { path: '/action-items', label: t('planning.actionItems'), icon: '✅', badge: stats.overdueActionItems },
    { path: '/hats', label: t('planning.hats') || 'Hats', icon: '🎩' },
    { path: '/employees', label: t('planning.employees') || 'Employees', icon: '👥' },
    { path: '/sales-report', label: t('planning.salesReport') || 'Sales', icon: '📊' },
    { path: '/dashboards', label: t('planning.dashboards') || 'Dashboards', icon: '📈' },
  ];

  const moreNavItems = [
    { path: '/povs', label: t('planning.povs') || 'POVs', icon: '👁️' },
    { path: '/metrics', label: t('planning.metrics') || 'Metrics', icon: '📊' },
    { path: '/visualizations', label: t('planning.visualizations') || 'Visualizations', icon: '📉' },
    { path: '/reports', label: t('planning.reports') || 'Reports', icon: '📄' },
    { path: '/action-states', label: t('planning.actionStates') || 'Action States', icon: '🔄' },
    { path: '/alerts', label: t('planning.alerts') || 'Alerts', icon: '🔔' },
    { path: '/manage-company', label: t('planning.manageCompany') || 'Settings', icon: '⚙️' },
  ];

  const allNavItems = [...navItems, ...moreNavItems];

  return (
    <div className="app-layout" dir={direction}>
      <header className="app-header">
        <div className="header-left">
          <button
            className="hamburger-btn"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? '✕' : '☰'}
          </button>
          <NavLink to="/plans" className="logo">
            <span className="logo-icon">🔑</span>
            <span className="logo-text">TurnKeyOS <span style={{color:'#3b82f6',fontWeight:700}}>Planning</span></span>
          </NavLink>

          {companies.length > 1 && (
            <select
              className="company-selector"
              value={currentCompany?.id || ''}
              onChange={(e) => selectCompany(e.target.value)}
            >
              {companies.map((company) => (
                <option key={company.id} value={company.id}>
                  {company.CompanyName}
                </option>
              ))}
            </select>
          )}
        </div>

        {/* Desktop nav */}
        <nav className="header-nav desktop-nav">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              className={`nav-link ${location.pathname === item.path ? 'active' : ''}`}
            >
              <span>{item.icon}</span>
              <span className="nav-label">{item.label}</span>
              {'badge' in item && (item as any).badge > 0 ? (
                <span className="nav-badge">{(item as any).badge}</span>
              ) : null}
            </NavLink>
          ))}
          <div className="nav-more">
            <button className="nav-link nav-more-btn">⋯</button>
            <div className="nav-more-dropdown">
              {moreNavItems.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  className={`nav-more-link ${location.pathname === item.path ? 'active' : ''}`}
                >
                  <span>{item.icon}</span>
                  <span>{item.label}</span>
                </NavLink>
              ))}
            </div>
          </div>
        </nav>

        <div className="header-right desktop-right">
          <select
            className="language-selector"
            value={locale}
            onChange={(e) => setLocale(e.target.value as SupportedLocale)}
          >
            {LANGUAGES.map((lang) => (
              <option key={lang.code} value={lang.code}>
                {lang.label}
              </option>
            ))}
          </select>
          <span className="org-name">{currentCompany?.CompanyName || ''}</span>
          <span className="user-name">{simpleUser?.displayName || simpleUser?.email}</span>
          <button className="btn btn-logout" onClick={handleSignOut}>
            {t('auth.signOut') || t('common.logout')}
          </button>
        </div>
      </header>

      {/* Mobile slide-down menu */}
      {mobileMenuOpen && (
        <div className="mobile-menu-overlay" onClick={() => setMobileMenuOpen(false)}>
          <nav className="mobile-menu" onClick={(e) => e.stopPropagation()}>
            <div className="mobile-menu-nav">
              {allNavItems.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  className={`mobile-nav-link ${location.pathname === item.path ? 'active' : ''}`}
                >
                  <span className="mobile-nav-icon">{item.icon}</span>
                  <span>{item.label}</span>
                  {'badge' in item && (item as any).badge > 0 ? (
                    <span className="nav-badge">{(item as any).badge}</span>
                  ) : null}
                </NavLink>
              ))}
            </div>
            <div className="mobile-menu-footer">
              <select
                className="language-selector"
                value={locale}
                onChange={(e) => setLocale(e.target.value as SupportedLocale)}
              >
                {LANGUAGES.map((lang) => (
                  <option key={lang.code} value={lang.code}>
                    {lang.label}
                  </option>
                ))}
              </select>
              <span className="mobile-user">{simpleUser?.displayName || simpleUser?.email}</span>
              <button className="btn btn-logout" onClick={handleSignOut}>
                {t('auth.signOut') || t('common.logout')}
              </button>
            </div>
          </nav>
        </div>
      )}

      <main className="app-main">
        <Outlet />
      </main>

      <style>{`
        .app-layout {
          min-height: 100vh;
          display: flex;
          flex-direction: column;
        }

        .app-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 0.75rem 1.5rem;
          background: var(--surface-color, #fff);
          border-bottom: 1px solid var(--border-color, #e0e0e0);
          box-shadow: var(--shadow-sm, 0 1px 3px rgba(0,0,0,0.12));
          position: sticky;
          top: 0;
          z-index: 50;
        }

        .header-left {
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        .hamburger-btn {
          display: none;
          background: none;
          border: none;
          font-size: 1.5rem;
          cursor: pointer;
          padding: 0.25rem;
          min-width: 44px;
          min-height: 44px;
          align-items: center;
          justify-content: center;
          border-radius: var(--radius-md, 8px);
          color: var(--text-primary, #212121);
        }

        .hamburger-btn:hover {
          background: var(--background-color, #f5f5f5);
        }

        .logo {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          text-decoration: none;
          color: var(--text-primary, #212121);
          font-weight: 700;
        }

        .logo-icon { font-size: 1.5rem; }
        .logo-text { font-size: 1.25rem; }

        .company-selector, .language-selector {
          padding: 0.5rem 0.75rem;
          border: 1px solid var(--border-color, #e0e0e0);
          border-radius: var(--radius-md, 8px);
          background: var(--surface-color, #fff);
          font-size: 0.875rem;
          min-height: 44px;
        }

        .language-selector {
          min-width: 80px;
        }

        .header-nav {
          display: flex;
          gap: 0.25rem;
        }

        .nav-link {
          display: flex;
          align-items: center;
          gap: 0.25rem;
          padding: 0.5rem 0.75rem;
          text-decoration: none;
          color: var(--text-secondary, #757575);
          border-radius: var(--radius-md, 8px);
          font-size: 0.875rem;
          transition: all 0.15s;
          white-space: nowrap;
        }

        .nav-link:hover {
          background: var(--background-color, #f5f5f5);
          color: var(--text-primary, #212121);
        }

        .nav-link.active {
          background: var(--primary-color, #1976d2);
          color: white;
        }

        .nav-label {
          display: inline;
        }

        .nav-more {
          position: relative;
        }

        .nav-more-btn {
          font-size: 1.25rem;
          line-height: 1;
        }

        .nav-more-dropdown {
          display: none;
          position: absolute;
          top: 100%;
          right: 0;
          background: var(--surface-color, #fff);
          border: 1px solid var(--border-color, #e0e0e0);
          border-radius: var(--radius-md, 8px);
          box-shadow: 0 4px 12px rgba(0,0,0,0.15);
          min-width: 200px;
          z-index: 100;
          padding: 0.25rem;
        }

        .nav-more:hover .nav-more-dropdown {
          display: flex;
          flex-direction: column;
        }

        .nav-more-link {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.5rem 0.75rem;
          text-decoration: none;
          color: var(--text-secondary, #757575);
          border-radius: var(--radius-sm, 4px);
          font-size: 0.875rem;
          transition: all 0.15s;
        }

        .nav-more-link:hover {
          background: var(--background-color, #f5f5f5);
          color: var(--text-primary, #212121);
        }

        .nav-more-link.active {
          background: var(--primary-color, #1976d2);
          color: white;
        }

        .nav-badge {
          background: var(--error-color, #d32f2f);
          color: white;
          padding: 2px 8px;
          border-radius: 12px;
          font-size: 0.75rem;
          font-weight: 600;
        }

        .header-right {
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        .org-name {
          font-weight: 500;
          font-size: 0.875rem;
        }

        .user-name {
          font-size: 0.875rem;
          color: var(--text-secondary, #757575);
        }

        .btn-logout {
          padding: 0.375rem 0.75rem;
          font-size: 0.75rem;
          color: var(--error-color, #d32f2f);
          border: 1px solid var(--error-color, #d32f2f);
          background: transparent;
          border-radius: var(--radius-md, 8px);
          cursor: pointer;
          transition: all 0.15s;
          min-height: 44px;
        }

        .btn-logout:hover {
          background: var(--error-color, #d32f2f);
          color: white;
        }

        .app-main {
          flex: 1;
          padding: 1.5rem;
        }

        /* Mobile menu overlay */
        .mobile-menu-overlay {
          display: none;
        }

        /* ==================== TABLET (769–1024px) ==================== */
        @media (max-width: 1024px) {
          .nav-label { display: none; }
          .nav-link { padding: 0.5rem; }
        }

        /* ==================== MOBILE (≤768px) ==================== */
        @media (max-width: 768px) {
          .app-header {
            padding: 0.5rem 0.75rem;
          }

          .hamburger-btn {
            display: flex;
          }

          .logo-text { display: none; }
          .logo-icon { font-size: 1.25rem; }

          .desktop-nav { display: none; }
          .desktop-right { display: none; }

          .app-main {
            padding: 0.75rem;
          }

          .mobile-menu-overlay {
            display: block;
            position: fixed;
            inset: 0;
            top: 57px; /* below header */
            background: rgba(0,0,0,0.4);
            z-index: 40;
            animation: fadeIn 0.15s ease;
          }

          .mobile-menu {
            background: var(--surface-color, #fff);
            max-height: calc(100vh - 57px);
            overflow-y: auto;
            -webkit-overflow-scrolling: touch;
            animation: slideDown 0.2s ease;
            box-shadow: 0 4px 12px rgba(0,0,0,0.15);
          }

          .mobile-menu-nav {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 2px;
            padding: 0.5rem;
          }

          .mobile-nav-link {
            display: flex;
            align-items: center;
            gap: 0.5rem;
            padding: 0.75rem;
            text-decoration: none;
            color: var(--text-secondary, #757575);
            border-radius: var(--radius-md, 8px);
            font-size: 0.875rem;
            min-height: 48px;
            transition: all 0.15s;
          }

          .mobile-nav-link:hover,
          .mobile-nav-link:active {
            background: var(--background-color, #f5f5f5);
          }

          .mobile-nav-link.active {
            background: var(--primary-color, #1976d2);
            color: white;
          }

          .mobile-nav-icon {
            font-size: 1.25rem;
          }

          .mobile-menu-footer {
            display: flex;
            align-items: center;
            gap: 0.75rem;
            padding: 0.75rem;
            border-top: 1px solid var(--border-color, #e0e0e0);
            flex-wrap: wrap;
          }

          .mobile-user {
            font-size: 0.8125rem;
            color: var(--text-secondary, #757575);
            flex: 1;
            min-width: 0;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }

          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }

          @keyframes slideDown {
            from { transform: translateY(-10px); opacity: 0; }
            to { transform: translateY(0); opacity: 1; }
          }
        }
      `}</style>
    </div>
  );
}
